import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { ArrowLeft, Check, Flame, MessageSquareText, Pin, Plus, Share2, ShieldCheck, Sparkles, Volume2, VolumeX, X, Zap } from "lucide-react";
import { api } from "../services/api";
import { drawVentVoices, type VentVoice } from "../services/ventVoices";
import { dartHitLabels, playDartHitSound, preloadInteractionSounds } from "../services/interactionSounds";
import type { SessionInfo, VentRitualResult } from "../types";
import { XhsShareModal } from "./XhsShareModal";

const CUSTOM_TARGET = "自定义对象";
const targets = ["难搞客户", "甩锅同事", "反复横跳的领导", "无限改需求的人", "已读不回又催进度的人", CUSTOM_TARGET];
const behaviorOptions = ["临时加需求", "说过的话不认", "一直压价", "情绪攻击", "拖款不付", "出问题就甩锅", "只催进度不给资源", "下班后反复轰炸"];
type PinPoint = { x:number; y:number };
type HitFeedback = PinPoint & { id:number; label:string };
type ConfettiStyle = CSSProperties & { "--tx":string; "--ty":string; "--fall":string; "--spin":string; "--delay":string };
type ShardStyle = CSSProperties & { "--shard-x":string; "--shard-y":string; "--shard-spin":string; "--shard-delay":string };

const confettiColors = ["#ffd45c", "#ff7058", "#62d6c4", "#8db7ff", "#f59ad4", "#fff2bd"];
const confettiPieces = Array.from({ length:96 }, (_, index) => {
  const angle = ((Math.PI * 2) / 96) * index + (index % 5) * 0.035;
  const horizontalDistance = 34 + (index % 8) * 4.5;
  const verticalDistance = 26 + (index % 7) * 3.5;
  return {
    id:index,
    style:{
      "--tx":`${Math.cos(angle) * horizontalDistance}vw`,
      "--ty":`${Math.sin(angle) * verticalDistance - 8}vh`,
      "--fall":`${38 + (index % 8) * 4}vh`,
      "--spin":`${(index % 2 ? -1 : 1) * (420 + index * 17)}deg`,
      "--delay":`${(index % 5) * 0.025}s`,
      backgroundColor:confettiColors[index % confettiColors.length],
    } as ConfettiStyle,
  };
});
const paperShards = Array.from({ length:30 }, (_, index) => {
  const angle = ((Math.PI * 2) / 30) * index + (index % 4) * 0.09;
  const distance = 105 + (index % 7) * 18;
  return {
    id:index,
    style:{
      "--shard-x":`${Math.cos(angle) * distance}px`,
      "--shard-y":`${Math.sin(angle) * distance}px`,
      "--shard-spin":`${(index % 2 ? -1 : 1) * (360 + index * 29)}deg`,
      "--shard-delay":`${0.06 + (index % 5) * 0.018}s`,
      backgroundColor:index % 3 === 0 ? "#ead8b4" : index % 3 === 1 ? "#d7bd8c" : "#b55a3d",
    } as ShardStyle,
  };
});

function playBoomSound() {
  if (!("AudioContext" in window)) return;
  try {
    const context = new AudioContext();
    const now = context.currentTime;
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-20, now);
    compressor.knee.setValueAtTime(10, now);
    compressor.ratio.setValueAtTime(8, now);
    compressor.attack.setValueAtTime(0.002, now);
    compressor.release.setValueAtTime(0.22, now);
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(1.75, now + 0.008);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.72);
    master.connect(compressor);
    compressor.connect(context.destination);

    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(185, now);
    oscillator.frequency.exponentialRampToValueAtTime(48, now + 0.65);
    oscillator.connect(master);

    const punch = context.createOscillator();
    const punchGain = context.createGain();
    punch.type = "square";
    punch.frequency.setValueAtTime(105, now);
    punch.frequency.exponentialRampToValueAtTime(38, now + 0.32);
    punchGain.gain.setValueAtTime(0.7, now);
    punchGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    punch.connect(punchGain);
    punchGain.connect(master);

    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * 0.72), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) {
      const decay = Math.pow(1 - index / data.length, 2.8);
      data[index] = (Math.random() * 2 - 1) * decay;
    }
    const noise = context.createBufferSource();
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2600, now);
    filter.frequency.exponentialRampToValueAtTime(180, now + 0.68);
    noise.buffer = buffer;
    noise.connect(filter);
    filter.connect(master);

    void context.resume();
    oscillator.start(now); oscillator.stop(now + 0.72);
    punch.start(now); punch.stop(now + 0.35);
    noise.start(now); noise.stop(now + 0.72);
    window.setTimeout(() => void context.close(), 950);
  } catch {
    // The seventh-pin animation still works if a browser blocks synthesized audio.
  }
}

export function VentRoom({ session, onNeedActivation, onQuotaChange, onBack }: { session:SessionInfo|null; onNeedActivation:()=>void; onQuotaChange:(quota:number)=>void; onBack:()=>void }) {
  const [target, setTarget] = useState(targets[0]);
  const [customTarget, setCustomTarget] = useState("");
  const [behaviors, setBehaviors] = useState<string[]>([behaviorOptions[0]]);
  const [customBehavior, setCustomBehavior] = useState("");
  const [vent, setVent] = useState("");
  const [result, setResult] = useState<VentRitualResult|null>(null);
  const [phase, setPhase] = useState<"setup"|"ritual"|"result">("setup");
  const [pinPoints, setPinPoints] = useState<PinPoint[]>([]);
  const [sessionVoices, setSessionVoices] = useState<VentVoice[]>([]);
  const [soundOn, setSoundOn] = useState(true);
  const [finaleActive, setFinaleActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [hitFeedback, setHitFeedback] = useState<HitFeedback|null>(null);
  const audioRefs = useRef<HTMLAudioElement[]>([]);
  const finaleTimerRef = useRef<number|null>(null);
  const voiceTimerRef = useRef<number|null>(null);
  const hitTimerRef = useRef<number|null>(null);
  const pins = pinPoints.length;
  const totalPins = Math.min(7, result?.roastLines.length || 7);
  const currentLine = useMemo(() => result?.roastLines[Math.max(0, pins - 1)] || "把火气留在这里，不把伤害带回现实。", [result, pins]);
  const effectiveTarget = target === CUSTOM_TARGET ? customTarget.trim() : target;

  useEffect(() => {
    preloadInteractionSounds();
    audioRefs.current = sessionVoices.map((voice) => { const audio = new Audio(voice.audio); audio.preload = "auto"; return audio; });
    return () => audioRefs.current.forEach((audio) => { audio.pause(); audio.src = ""; });
  }, [sessionVoices]);

  useEffect(() => () => {
    if (finaleTimerRef.current !== null) window.clearTimeout(finaleTimerRef.current);
    if (voiceTimerRef.current !== null) window.clearTimeout(voiceTimerRef.current);
    if (hitTimerRef.current !== null) window.clearTimeout(hitTimerRef.current);
  }, []);

  function toggleBehavior(value:string) {
    setBehaviors((previous) => previous.includes(value) ? previous.filter((item) => item !== value) : previous.length < 4 ? [...previous, value] : previous);
  }
  function addCustomBehavior() {
    const value = customBehavior.trim();
    if (value.length < 2) return setError("自定义破事至少写 2 个字");
    if (behaviors.length >= 4) return setError("最多选择 4 件破事");
    if (!behaviors.includes(value)) setBehaviors((previous) => [...previous, value]);
    setCustomBehavior(""); setError("");
  }
  async function createRitual() {
    if (!session) return onNeedActivation();
    if (effectiveTarget.length < 2) return setError("请填写 2—20 字的对象类型，不要写真人姓名");
    if (!behaviors.length) return setError("至少选择一件想扎掉的破事");
    setLoading(true); setError("");
    try {
      const response = await api.ventRitual({ target:effectiveTarget, behaviors, vent });
      setSessionVoices(drawVentVoices()); setResult(response.ritual); onQuotaChange(response.quotaLeft); setPinPoints([]); setFinaleActive(false); setPhase("ritual");
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "仪式生成失败";
      setError(message);
      if (message.includes("激活")) onNeedActivation();
    }
    finally { setLoading(false); }
  }
  function playVoice(index:number) {
    if (!soundOn) return;
    audioRefs.current.forEach((audio) => { audio.pause(); audio.currentTime = 0; });
    const audio = audioRefs.current[index];
    if (audio) void audio.play().catch(() => { /* vibration and text remain available when autoplay is restricted */ });
  }
  function pinIt(event?:React.PointerEvent<HTMLDivElement>) {
    if (!result || pins >= totalPins || finaleActive) return;
    event?.preventDefault();
    const point = event ? (() => { const rect=event.currentTarget.getBoundingClientRect(); return {x:Math.max(4,Math.min(96,((event.clientX-rect.left)/rect.width)*100)),y:Math.max(4,Math.min(96,((event.clientY-rect.top)/rect.height)*100))}; })() : {x:50,y:45};
    const next = pins + 1;
    setPinPoints((previous)=>[...previous,point]);
    const hitLabel = dartHitLabels[crypto.getRandomValues(new Uint8Array(1))[0] % dartHitLabels.length];
    setHitFeedback({ ...point, id:Date.now() + next, label:hitLabel });
    if (hitTimerRef.current !== null) window.clearTimeout(hitTimerRef.current);
    hitTimerRef.current = window.setTimeout(() => setHitFeedback(null), 360);
    if (event) {
      const element = event.currentTarget;
      const base = getComputedStyle(element).transform;
      const initial = base === "none" ? "" : base;
      element.animate([
        { transform:`${initial} scale(1) rotate(0deg)` },
        { transform:`${initial} scale(.955) rotate(${next % 2 ? -1.8 : 1.8}deg)`, offset:.34 },
        { transform:`${initial} scale(1.025) rotate(${next % 2 ? 1 : -1}deg)`, offset:.68 },
        { transform:`${initial} scale(1) rotate(0deg)` },
      ], { duration:280, easing:"cubic-bezier(.2,.8,.2,1)" });
    }
    if (voiceTimerRef.current !== null) window.clearTimeout(voiceTimerRef.current);
    audioRefs.current.forEach((audio) => { audio.pause(); audio.currentTime = 0; });
    if (soundOn) playDartHitSound(next - 1);
    if (next === totalPins) {
      setFinaleActive(true);
      if (soundOn) window.setTimeout(playBoomSound, 105);
      voiceTimerRef.current = window.setTimeout(() => playVoice(next - 1), 850);
      navigator.vibrate?.([22,18,85,28,160]);
      finaleTimerRef.current = window.setTimeout(() => { setFinaleActive(false); setPhase("result"); }, 2300);
    } else {
      voiceTimerRef.current = window.setTimeout(() => playVoice(next - 1), 700);
      navigator.vibrate?.(22);
    }
  }

  return <section className="vent-room">
    <header className="vent-nav"><button className="back" onClick={onBack}><ArrowLeft size={18}/>返回职场答案之书</button><span><Flame/>职场泄压室</span></header>
    {phase === "setup" && <div className="vent-setup">
      <div className="vent-intro"><p className="eyebrow">只扎行为 · 只针对烦心事</p><h1>今天又碰到了什么<br/><em>离谱操作？</em></h1><p>先用一场情绪释放练习把气出了，再拿一套现实话术把事情解决。吐槽仅用于本次 AI 生成，不保存，也不会出现在分享图里。</p></div>
      <div className="vent-form">
        <label className="field"><span>今天碰到的是</span><select value={target} onChange={(event)=>setTarget(event.target.value)}>{targets.map((item)=><option key={item}>{item}</option>)}</select></label>
        {target===CUSTOM_TARGET&&<label className="field custom-field"><span>自定义对象类型 <em>{customTarget.length}/20</em></span><input maxLength={20} value={customTarget} onChange={(event)=>setCustomTarget(event.target.value)} placeholder="例如：推卸责任的供应商（不要写真人姓名）"/></label>}
        <fieldset><legend>想扎掉哪些破事？<small>最多选择 4 项</small></legend><div className="behavior-grid">{behaviorOptions.map((item)=><button type="button" key={item} className={behaviors.includes(item)?"active":""} onClick={()=>toggleBehavior(item)}>{behaviors.includes(item)&&<Check size={14}/>} {item}</button>)}</div>
          <div className="custom-behavior"><input maxLength={24} value={customBehavior} onChange={(event)=>setCustomBehavior(event.target.value)} onKeyDown={(event)=>event.key==="Enter"&&addCustomBehavior()} placeholder="自定义一件破事…"/><button type="button" disabled={behaviors.length>=4} onClick={addCustomBehavior}><Plus/>加入</button></div>
          {!!behaviors.filter((item)=>!behaviorOptions.includes(item)).length&&<div className="custom-tags">{behaviors.filter((item)=>!behaviorOptions.includes(item)).map((item)=><button type="button" key={item} onClick={()=>toggleBehavior(item)}>{item}<X/></button>)}</div>}
        </fieldset>
        <label className="field"><span>想吐槽一句（可选，不保存）<em>{vent.length}/200</em></span><textarea maxLength={200} value={vent} onChange={(event)=>setVent(event.target.value)} placeholder="把最憋屈的一句话留在这里……"/></label>
        <div className="privacy-note"><ShieldCheck/><span>不要填写真人姓名、公司、电话或其他个人信息。只扎行为，只针对烦心事。</span></div>
        {error&&<p className="error">{error}</p>}
        <button className="vent-pay" disabled={loading||!behaviors.length} onClick={()=>void createRitual()}><Flame/>{loading?"正在制作专属仪式…":"生成专属消气仪式 · 1次"}</button>
        <small className="charge-note">包含：完整情绪释放互动、专属吐槽反馈、现实处理话术和小红书分享图</small>
      </div>
    </div>}

    {phase === "ritual" && result && <div className={`ritual-stage ${pins===totalPins?"complete":""}`}>
      <div className="ritual-toolbar"><p className="eyebrow">{pins === totalPins ? "封印完成" : `第 ${pins + 1} 针 · 共 ${totalPins} 针`}</p><button onClick={()=>setSoundOn((value)=>!value)}>{soundOn?<Volume2/>:<VolumeX/>}{soundOn?"声音已开":"声音已关"}</button></div><h1>{result.ritualTitle}</h1>
      <p className="pin-hint">点击任意位置 · 咻声、命中、大叫、回弹针针到位</p>
      <div className={`paper-person ${hitFeedback ? "yelling" : ""}`} onPointerDown={pinIt} role="button" tabIndex={0} onKeyDown={(event)=>event.key==="Enter"&&pinIt()} aria-label="点击纸片人任意位置释放烦心事">
        <div className="paper-head"><i/><i/></div><div className="paper-body"><span>破<br/>事</span></div><div className="paper-arm left"/><div className="paper-arm right"/><div className="paper-leg left"/><div className="paper-leg right"/>
        {pinPoints.map((point,index)=><span className="digital-pin" style={{left:`${point.x}%`,top:`${point.y}%`}} key={index}><Pin/></span>)}
        {hitFeedback&&<span className="hit-burst" key={hitFeedback.id} style={{left:`${hitFeedback.x}%`,top:`${hitFeedback.y}%`}} aria-hidden="true"><i/><b>{hitFeedback.label}</b></span>}
        {hitFeedback&&<span className="relief-pop" key={`relief-${hitFeedback.id}`} aria-hidden="true">解气值 +1</span>}
        {finaleActive&&<span className="paper-shards" aria-hidden="true">{paperShards.map((shard)=><i key={shard.id} style={shard.style}/>)}</span>}
      </div>
      {finaleActive&&<div className="finale-effects" aria-hidden="true"><span className="finale-flash"/><b className="boom-word">嘣！</b><span className="confetti-burst">{confettiPieces.map((piece)=><i key={piece.id} style={piece.style}/>)}</span></div>}
      <div className="voice-reply"><Volume2/><span>小人回应</span><b>“{pins ? sessionVoices[pins-1]?.text : "您尽管落针，我听着呢"}”</b></div>
      <div className="ritual-line"><Zap/>{currentLine}</div>
      {pins===totalPins&&<p className="transition-copy">气到这里为止。现在，把主动权拿回来。</p>}
    </div>}

    {phase === "result" && result && <div className="vent-result">
      <p className="eyebrow">情绪已经处理 · 接下来处理事情</p><h1>{result.reliefMessage}</h1>
      <div className="relief-score"><span>今日解气值</span><b>MAX</b><i/></div>
      <div className="vent-solution"><article><Sparkles/><div><small>现实处理策略</small><h2>{result.actionSummary}</h2></div></article><section><p>可以直接复制的话术</p><blockquote>{result.responseScript}</blockquote></section><section><p>需要留下的证据</p><ul>{result.evidenceSteps.map((item)=><li key={item}><Check/>{item}</li>)}</ul></section><p className="stop-line"><b>止损线：</b>{result.stopRule}</p></div>
      <div className="vent-actions"><button className="xhs-share" onClick={()=>setShareOpen(true)}><Share2/>把今日封印分享到小红书</button><button className="secondary" onClick={()=>{setPhase("setup");setResult(null);setPinPoints([]);setSessionVoices([]);setFinaleActive(false)}}><MessageSquareText/>再吐槽一件</button></div>
      <XhsShareModal key={shareOpen?"vent-open":"vent-closed"} open={shareOpen} onClose={()=>setShareOpen(false)} scenario="职场关系" cards={[]} ventShare={{target:effectiveTarget,behaviors,ritual:result,pinPoints}} />
    </div>}
  </section>;
}
